/**
 * Console capture for session replay.
 *
 * Two of the replay engine's four lanes are console output — one per context —
 * because the causal chain a developer follows usually crosses them: the EMR
 * warns, the panel throws, the retry succeeds. Without both, the timeline shows
 * an effect with no cause.
 *
 * This taps the Comprehend app's own console. Injector console messages
 * arrive through the extension runtime or embed bridge because that console
 * belongs to a document we cannot reach from here.
 *
 * Installed once and left installed: uninstalling would mean restoring a
 * console that other code may have wrapped in the meantime, and the tap is inert
 * when nothing is recording (noteConsole no-ops without a live recorder).
 */

import { noteConsole } from './sidecar';

const LEVELS = ['log', 'info', 'warn', 'error'];
const REPLAY_INTERNAL_PREFIXES = ['replay:', '[cpt-replay]'];

let installed = false;

export function isReplayInternalMessage(message) {
  return (
    typeof message === 'string' &&
    REPLAY_INTERNAL_PREFIXES.some((prefix) => message.startsWith(prefix))
  );
}

/** Render one console argument as a short string, never throwing. */
function describe(value) {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.stack || `${value.name}: ${value.message}`;
  try {
    return JSON.stringify(value);
  } catch (e) {
    // Circular, a throwing getter, a DOM node…
    try {
      return String(value);
    } catch (e2) {
      return '[unrenderable]';
    }
  }
}

export function installConsoleTap() {
  if (installed || typeof window === 'undefined' || !window.console) return;
  installed = true;

  for (const level of LEVELS) {
    const original = window.console[level];
    if (typeof original !== 'function') continue;
    window.console[level] = function (...args) {
      if (isReplayInternalMessage(args[0])) {
        if (process.env.NODE_ENV !== 'production') {
          return original.apply(this, args);
        }
        return;
      }
      try {
        noteConsole({ source: 'cx', level, message: args.map(describe).join(' ') });
      } catch (e) {
        /* a diagnostic stream must never break logging */
      }
      return original.apply(this, args);
    };
  }

  // Uncaught errors never reach console.error as a call, so they are captured
  // separately — and they are the ones worth seeing on the timeline.
  window.addEventListener('error', (event) => {
    noteConsole({
      source: 'cx',
      level: 'error',
      message: `Uncaught ${event.message}`,
      stack: event.error?.stack || `${event.filename}:${event.lineno}`,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    noteConsole({
      source: 'cx',
      level: 'error',
      message: `Unhandled rejection: ${describe(event.reason)}`,
      stack: event.reason?.stack || null,
    });
  });

  window.chrome?.runtime?.onMessage?.addListener((request) => {
    if (request?.type !== 'CONSOLE_ERROR') return;
    if (isReplayInternalMessage(request.log?.message)) return;
    noteConsole({
      source: 'injector',
      level: request.log?.type,
      message: describe(request.log?.message),
    });
  });

  window.addEventListener('message', (event) => {
    if (
      window === window.parent ||
      event.source !== window.parent ||
      !event.data?.__comp ||
      event.data.type !== 'CONSOLE_ERROR'
    ) return;
    if (isReplayInternalMessage(event.data.payload?.message)) return;
    noteConsole({
      source: 'injector',
      level: event.data.payload?.type,
      message: describe(event.data.payload?.message),
    });
  });
}
